/**
 * AdMesh UI SDK - Main Entry Point
 * 
 * Zero-code integration for displaying AdMesh recommendations
 */

// Import Tailwind CSS for markdown styling
import './styles/globals.css';

// Core SDK
export { AdMeshSDK } from './sdk/AdMeshSDK';
export type { AdMeshSDKConfig, ShowRecommendationsOptions } from './sdk/AdMeshSDK';

// Tracking
export { AdMeshTracker } from './sdk/AdMeshTracker';
export type { TrackerConfig } from './sdk/AdMeshTracker';

// Rendering
export { AdMeshRenderer } from './sdk/AdMeshRenderer';
export type { RenderOptions } from './sdk/AdMeshRenderer';

// ACIE API integration
export { callAnalyzeIntentEndpoint } from './utils/acieApi';
export type { 
  CommercialIntentRequest, 
  CommercialIntentResult
} from './utils/acieApi';

// Weave Response Processing
export { WeaveResponseProcessor } from './sdk/WeaveResponseProcessor';
export type { DetectedLink, ProcessorConfig } from './sdk/WeaveResponseProcessor';

// Weave format is now integrated into AdMeshSDK
// Use format: 'weave' option in showRecommendations() method

// Provider Pattern (Simplified Integration)
export { AdMeshProvider } from './context/AdMeshProvider';
export type { AdMeshProviderProps } from './context/AdMeshProvider';

// Components
export { AdMeshRecommendations } from './components/AdMeshRecommendations';
export type { AdMeshRecommendationsProps } from './components/AdMeshRecommendations';
export { WeaveFallbackRecommendations } from './components/WeaveFallbackRecommendations';
export type { WeaveFallbackRecommendationsProps } from './components/WeaveFallbackRecommendations';

// Context
export { AdMeshContext, useAdMeshContext } from './context/AdMeshContext';
export type { AdMeshContextValue } from './context/AdMeshContext';
export { WeaveAdFormatProvider, useWeaveAdFormatContext } from './context/WeaveAdFormatContext';
export type { WeaveAdFormatContextType } from './context/WeaveAdFormatContext';

// Components
export { AdMeshLayout } from './components/AdMeshLayout';
export { AdMeshTailAd } from './components/AdMeshTailAd';
export type { AdMeshTailAdProps } from './components/AdMeshTailAd';
export { AdMeshCompactCard } from './components/AdMeshCompactCard';
export { AdMeshFollowup } from './components/AdMeshFollowup';
export type { AdMeshFollowupProps } from './components/AdMeshFollowup';
export { AdMeshViewabilityTracker } from './components/AdMeshViewabilityTracker';
export { AdMeshLinkTracker } from './components/AdMeshLinkTracker';
export { AdMeshBadge } from './components/AdMeshBadge';
export { AdMeshIntentAssistant } from './components/AdMeshIntentAssistant';
export type { AdMeshIntentAssistantProps } from './components/AdMeshIntentAssistant';
export { WeaveAdFormatContainer } from './components/WeaveAdFormatContainer';
export type { WeaveAdFormatContainerProps } from './components/WeaveAdFormatContainer';

// Hooks
export { useAdMesh } from './hooks/useAdMesh';
export { useAdMeshStyles } from './hooks/useAdMeshStyles';
export { useViewabilityTracker } from './hooks/useViewabilityTracker';
export { useWeaveAdFormat } from './hooks/useWeaveAdFormat';
export type { UseWeaveAdFormatOptions } from './hooks/useWeaveAdFormat';

// Types
export type { AdMeshTheme, PlatformResponse, DelegationActivationPayload, ActiveDelegationSession } from './types/index';

// Streaming Events (for event-driven link detection)
export {
  dispatchStreamingStartEvent,
  dispatchStreamingCompleteEvent,
  onStreamingStart,
  onStreamingComplete,
  STREAMING_START_EVENT,
  STREAMING_COMPLETE_EVENT
} from './utils/streamingEvents';
export type {
  StreamingStartEventDetail,
  StreamingCompleteEventDetail
} from './utils/streamingEvents';

// Inline exposure tracking helper
export {
  createInlineExposureTracker
} from './utils/inlineExposureTracker';
export type {
  InlineExposureTracker,
  InlineExposureTrackingParams
} from './utils/inlineExposureTracker';

// Headings extraction utility
export { extractHeadingsDirectly } from './utils/extractHeadings';
export type { HeadingsExtractionResult } from './utils/extractHeadings';

// Version
export const VERSION = '1.0.31';
